import * as React from "react";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { cn } from "@racoon/ui";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

export function Pagination({ currentPage, totalPages, onPageChange }: PaginationProps) {
  const pages = buildPageRange(currentPage, totalPages);

  return (
    <div className="w-full overflow-x-auto">
      <div className="flex items-center justify-center gap-[5px] w-max mx-auto px-2">
        <NavButton onClick={() => onPageChange(1)} disabled={currentPage <= 1} aria-label="First page" data-testid="pagination-first">
          <ChevronsLeft className="h-4 w-4" />
        </NavButton>
        <NavButton onClick={() => onPageChange(currentPage - 1)} disabled={currentPage <= 1} aria-label="Previous page" data-testid="pagination-previous">
          <ChevronLeft className="h-4 w-4" />
        </NavButton>

        {pages.map((p, i) =>
          p === "..." ? (
            <span
              key={`ellipsis-${i}`}
              data-testid="pagination-ellipsis"
              className="flex items-center justify-center w-8 h-8 text-sm text-gray-400 select-none"
            >
              ...
            </span>
          ) : (
            <PageButton key={p} page={p} isActive={p === currentPage} onClick={() => onPageChange(p)} />
          ),
        )}

        <NavButton onClick={() => onPageChange(currentPage + 1)} disabled={currentPage >= totalPages} aria-label="Next page" data-testid="pagination-next">
          <ChevronRight className="h-4 w-4" />
        </NavButton>
        <NavButton onClick={() => onPageChange(totalPages)} disabled={currentPage >= totalPages} aria-label="Last page" data-testid="pagination-last">
          <ChevronsRight className="h-4 w-4" />
        </NavButton>
      </div>
    </div>
  );
}

interface NavButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
}

function NavButton({ children, disabled, className, ...props }: NavButtonProps) {
  return (
    <button
      {...props}
      disabled={disabled}
      className={cn(
        "flex items-center justify-center w-8 h-8 rounded-lg text-gray-600",
        "border border-[#E5E7EB] bg-white transition-colors hover:bg-gray-100",
        disabled && "opacity-40 pointer-events-none",
        className,
      )}
    >
      {children}
    </button>
  );
}

interface PageButtonProps {
  page: number;
  isActive: boolean;
  onClick: () => void;
}

function PageButton({ page, isActive, onClick }: PageButtonProps) {
  return (
    <button
      onClick={onClick}
      data-testid="pagination-page"
      aria-current={isActive ? "page" : undefined}
      className={cn(
        "flex items-center justify-center w-8 h-8 rounded-lg text-[13px] font-semibold transition-colors",
        isActive
          ? "bg-[#2F80ED] text-white shadow-sm"
          : "border border-[#E5E7EB] bg-white text-[#333333] hover:bg-gray-100",
      )}
    >
      {page}
    </button>
  );
}

function buildPageRange(current: number, total: number): (number | "...")[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages: (number | "...")[] = [1];
  const leftSibling = Math.max(2, current - 1);
  const rightSibling = Math.min(total - 1, current + 1);
  if (leftSibling > 2) pages.push("...");
  for (let i = leftSibling; i <= rightSibling; i++) pages.push(i);
  if (rightSibling < total - 1) pages.push("...");
  pages.push(total);
  return pages;
}
